import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { StatusPanel } from "../components/StatusPanel";
import { getPayoutDestination, listAdminPayouts, updatePayoutStatus } from "../lib/payoutAdminService";
import type { PayoutStatus } from "../lib/workflowTypes";
import "../member-final.css";

export function PayoutAdminPage() {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [destination, setDestination] = useState("");
  const [reference, setReference] = useState("");
  const [failureReason, setFailureReason] = useState("");
  const payouts = useQuery({ queryKey: ["admin-payouts"], queryFn: listAdminPayouts });
  const details = useMutation({
    mutationFn: getPayoutDestination,
    onSuccess: (result) => setDestination(result.destination),
  });
  const update = useMutation({
    mutationFn: ({ status }: { status: PayoutStatus }) => updatePayoutStatus(selectedId!, status, reference || undefined, failureReason || undefined),
    onSuccess: async () => {
      setSelectedId(null); setDestination(""); setReference(""); setFailureReason("");
      await queryClient.invalidateQueries({ queryKey: ["admin-payouts"] });
    },
  });
  const selected = payouts.data?.find((item) => item.id === selectedId);

  function select(id: string) {
    setSelectedId(id);
    setDestination("");
    details.mutate(id);
  }

  return (
    <div className="member-app standalone-member-page">
      <header className="member-header"><Link className="member-logo" to="/admin"><span>H</span><b>PAYOUT OPERATIONS</b></Link><div className="member-header-actions"><Link to="/admin">ADMIN HOME</Link><Link to="/app">MEMBER APP</Link></div></header>
      <main>
        <section className="standalone-member-hero"><span>MANUAL PAYMENT QUEUE</span><h1>Verify once.<br />Pay exactly.</h1><p>Every destination is encrypted. Reveal it only when you are ready to pay through Wise, PayPal, or USDT on Tron.</p></section>
        {(details.isError || update.isError) && <StatusPanel title="Payout action blocked" message={(details.error ?? update.error)?.message ?? "Try again."} kind="error" />}
        <section className="admin-payout-layout">
          <div className="admin-payout-list">
            {(payouts.data ?? []).map((item) => <button className={item.id === selectedId ? "active" : ""} key={item.id} onClick={() => select(item.id)}><div><b>{item.memberHandle}</b><span>{item.method.replace("_", " ").toUpperCase()} · {item.destinationMasked}</span></div><div><strong>${item.amountUsd.toFixed(2)}</strong><small>{item.status.replace("_", " ")}</small></div></button>)}
          </div>
          <div className="admin-payout-detail">
            {selected ? <>
              <span className="member-kicker">{selected.method.replace("_", " ").toUpperCase()}</span><h2>${selected.amountUsd.toFixed(2)}</h2>
              {selected.guardianRequired && !selected.guardianApproved && <StatusPanel title="Guardian approval required" message="Do not approve or pay this request until guardian approval is recorded." kind="error" />}
              <label><span>Verified destination</span><input readOnly value={destination || (details.isPending ? "Decrypting…" : "")} /></label>
              <label><span>Provider reference / transaction hash</span><input value={reference} onChange={(event) => setReference(event.target.value)} /></label>
              <label><span>Failure reason</span><textarea value={failureReason} onChange={(event) => setFailureReason(event.target.value)} /></label>
              <div className="member-actions">
                <button disabled={selected.guardianRequired && !selected.guardianApproved} onClick={() => update.mutate({ status: "approved" })}>APPROVE</button>
                <button disabled={selected.guardianRequired && !selected.guardianApproved} onClick={() => update.mutate({ status: "processing" })}>MARK PROCESSING</button>
                <button className="primary" disabled={(selected.guardianRequired && !selected.guardianApproved) || reference.trim().length < 3} onClick={() => update.mutate({ status: "paid" })}>MARK PAID</button>
                <button className="danger" disabled={failureReason.trim().length < 5} onClick={() => update.mutate({ status: "failed" })}>MARK FAILED</button>
              </div>
            </> : <div className="member-empty"><span>$</span><h3>Select a request.</h3><p>Check the member, guardian state, destination, and amount before paying.</p></div>}
          </div>
        </section>
      </main>
    </div>
  );
}
